/**
 * Scroll Depth Tracking Hook
 * Tracks scroll depth milestones (25%, 50%, 75%, 100%)
 * Fires analytics event once per milestone per page
 */

/// <reference path="../types/global.d.ts" />
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';

type ScrollMilestone = 25 | 50 | 75 | 100;

interface ScrollDepthEvent {
  milestone: ScrollMilestone;
  pageUrl: string;
  pageTitle: string;
  timeToReach: number; // milliseconds since page load
}

// Debounce helper
function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  }) as T;
}

export function useScrollDepth(onMilestone?: (event: ScrollDepthEvent) => void) {
  const [location] = useLocation();
  const pageLoadTime = useRef<number>(Date.now());
  const reachedMilestones = useRef<Set<ScrollMilestone>>(new Set());
  const lastPathname = useRef<string>(location);

  // Reset milestones when page changes
  useEffect(() => {
    if (location !== lastPathname.current) {
      reachedMilestones.current = new Set();
      pageLoadTime.current = Date.now();
      lastPathname.current = location;
    }
  }, [location]);

  const trackMilestone = useCallback((milestone: ScrollMilestone) => {
    if (reachedMilestones.current.has(milestone)) {
      return; // Already tracked this milestone
    }

    reachedMilestones.current.add(milestone);

    const event: ScrollDepthEvent = {
      milestone,
      pageUrl: window.location.href,  // Use full URL to match page_view events
      pageTitle: document.title,
      timeToReach: Date.now() - pageLoadTime.current,
    };

    // Call custom handler if provided
    if (onMilestone) {
      onMilestone(event);
    }

    // Fire analytics event
    if (typeof window !== 'undefined' && window.trackScrollDepth) {
      window.trackScrollDepth(event);
    }
  }, [onMilestone]);

  const calculateScrollDepth = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;

    // Handle case where page is not scrollable
    if (scrollHeight <= clientHeight) {
      // Page fits in viewport - consider it 100% scrolled
      trackMilestone(25);
      trackMilestone(50);
      trackMilestone(75);
      trackMilestone(100);
      return;
    }

    const scrollableHeight = scrollHeight - clientHeight;
    const scrollPercentage = (scrollTop / scrollableHeight) * 100;

    // Track milestones in order
    if (scrollPercentage >= 25 && !reachedMilestones.current.has(25)) {
      trackMilestone(25);
    }
    if (scrollPercentage >= 50 && !reachedMilestones.current.has(50)) {
      trackMilestone(50);
    }
    if (scrollPercentage >= 75 && !reachedMilestones.current.has(75)) {
      trackMilestone(75);
    }
    if (scrollPercentage >= 100 && !reachedMilestones.current.has(100)) {
      trackMilestone(100);
    }
  }, [trackMilestone]);

  useEffect(() => {
    // Debounce scroll handler for performance (100ms)
    const handleScroll = debounce(calculateScrollDepth, 100);

    // Check initial scroll position (for pages that load scrolled)
    calculateScrollDepth();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [calculateScrollDepth]);

  return {
    reachedMilestones: reachedMilestones.current,
  };
}
